import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "../../api/apiClient";
import {
  createCaseEndpointApiV1CasesPost,
  deleteCaseEndpointApiV1CasesCaseIdDelete,
  extractExamineeAndClaimNumberApiV1CasesExtractInfoPost,
  updateCaseEndpointApiV1CasesCaseIdPatch,
} from "../../api/generated/sdk.gen";
import type {
  BodyCreateCaseEndpointApiV1CasesPost,
  UpdateCaseEndpointApiV1CasesCaseIdPatchData,
} from "../../api/generated/types.gen";
import { CLIENT_KEYS } from "../../constants/clientKeys";

export const useCreateCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: FormData) => {
      // Convert FormData to the expected structure
      const data = {
        template_id: formData.get("template_id") as string,
        file: formData.get("file") as File,
        examinee_name: (formData.get("examinee_name") as string) || "",
        claim_number: (formData.get("claim_number") as string) || "",
        report_due_date: (formData.get("report_due_date") as string) || undefined,
        appointment_date: (formData.get("appointment_date") as string) || undefined,
        organisation: (formData.get("organisation") as string) || undefined,
        service_type: (formData.get("service_type") as string) || undefined,
        // MDRS-234: admin creating on a doctor's behalf. Omitted → the caller
        // owns the case (the backend ignores it for non-admins).
        doctor_email: (formData.get("doctor_email") as string) || undefined,
      } as BodyCreateCaseEndpointApiV1CasesPost;

      const response = await createCaseEndpointApiV1CasesPost({
        body: data,
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(`Failed to create case: ${JSON.stringify(response.error)}`);
      }

      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch cases query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.CASES],
      });
    },
    onError: (error) => {
      console.error("Error creating case:", error);
    },
  });
};

export const useExtractExamineeAndClaimNumber = () => {
  return useMutation({
    mutationFn: async (file: File) => {
      // The backend expects a file upload (FormData with "file" key)
      // Optionally, you can add other fields if needed
      const response = await extractExamineeAndClaimNumberApiV1CasesExtractInfoPost({
        body: { file },
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to extract examinee and claim number: ${JSON.stringify(
            response.error
          )}`
        );
      }

      return response.data;
    },
    onError: (error) => {
      console.error("Error extracting examinee and claim number:", error);
    },
  });
};

export const useDeleteCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (caseId: string) => {
      const response = await deleteCaseEndpointApiV1CasesCaseIdDelete({
        path: { case_id: caseId },
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(`Failed to delete case: ${JSON.stringify(response.error)}`);
      }

      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch cases query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.CASES],
      });
    },
    onError: (error) => {
      console.error("Error deleting case:", error);
    },
  });
};

export type DeleteCaseOutcome =
  | { caseId: string; outcome: "deleted" }
  | { caseId: string; outcome: "already-gone" }
  | { caseId: string; outcome: "failed"; error: string };

// MDRS-30: 404 is a valid terminal state for a delete. A bulk delete that
// retries (e.g. with a stale selection after a partially-successful prior
// attempt) shouldn't surface "Case not found" as a hard failure — the user's
// intent was "make this gone" and it already is. Genuine errors (5xx, network
// abort) still bubble up so we don't silently swallow real problems.
export const classifyDeleteCaseResponse = (
  caseId: string,
  response: {
    data?: unknown;
    error?: unknown;
    response?: { status?: number };
  }
): DeleteCaseOutcome => {
  if (!("error" in response) || !response.error) {
    return { caseId, outcome: "deleted" };
  }
  if (response.response?.status === 404) {
    return { caseId, outcome: "already-gone" };
  }
  return {
    caseId,
    outcome: "failed",
    error: `Failed to delete case ${caseId}: ${JSON.stringify(response.error)}`,
  };
};

export type BulkDeleteCasesResult = {
  outcomes: DeleteCaseOutcome[];
  deletedCount: number;
  alreadyGoneCount: number;
  failedCount: number;
};

const summariseOutcomes = (
  outcomes: DeleteCaseOutcome[]
): BulkDeleteCasesResult => ({
  outcomes,
  deletedCount: outcomes.filter((o) => o.outcome === "deleted").length,
  alreadyGoneCount: outcomes.filter((o) => o.outcome === "already-gone").length,
  failedCount: outcomes.filter((o) => o.outcome === "failed").length,
});

export const useDeleteMultipleCases = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (caseIds: string[]): Promise<BulkDeleteCasesResult> => {
      const outcomes: DeleteCaseOutcome[] = [];

      // Sequential to avoid overwhelming the server. Each iteration converts
      // its raw response (or thrown network error) into a single
      // DeleteCaseOutcome so the aggregate logic below stays uniform.
      for (const caseId of caseIds) {
        try {
          const response = await deleteCaseEndpointApiV1CasesCaseIdDelete({
            path: { case_id: caseId },
            client: apiClient,
          });
          outcomes.push(classifyDeleteCaseResponse(caseId, response));
        } catch (error) {
          outcomes.push({
            caseId,
            outcome: "failed",
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }

      // Partial-success returns instead of throws — callers branch on
      // `failedCount` to decide which toast to show. Throwing here would
      // skip the cache invalidation + selection reset below, leaving the
      // user with a stale UI and stale selection on the next click.
      return summariseOutcomes(outcomes);
    },
    // ``onSettled`` not ``onSuccess``: even when some deletes fail, the ones
    // that succeeded are gone from the DB and must be removed from the
    // visible list. Otherwise the user sees ghost rows that the next click
    // 404s on.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.CASES],
      });
    },
    onError: (error) => {
      // Only fires on a thrown error from mutationFn (network blip in our
      // own code rather than a per-case failure). Per-case failures live in
      // BulkDeleteCasesResult.failedCount.
      console.error("Error deleting multiple cases:", error);
    },
  });
};

export const useUpdateCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      caseId: string;
      updateData: UpdateCaseEndpointApiV1CasesCaseIdPatchData["query"];
    }) => {
      const { caseId, updateData } = data;

      const response = await updateCaseEndpointApiV1CasesCaseIdPatch({
        path: { case_id: caseId },
        query: updateData,
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(`Failed to update case: ${JSON.stringify(response.error)}`);
      }

      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch cases query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.CASES],
      });
    },
    onError: (error) => {
      console.error("Error updating case:", error);
    },
  });
};
